import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Crown, Wallet, Activity } from "lucide-react";

const TITLES = {
  "/panel": "Dashboard",
  "/checker": "Discord ID Sorgu",
  "/magaza": "Mağaza",
  "/duyurular": "Duyurular",
  "/admin": "Admin Konsolu",
};

export default function TopBar() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const title = TITLES[pathname] || "Gulyan Checker";
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    const load = () =>
      api.get("/stats/overview").then((r) => setQuota(r.data)).catch(() => {});
    load();
    const i = setInterval(load, 15000);
    return () => clearInterval(i);
  }, [pathname]);

  const dailyLimit = quota?.daily_limit ?? 5;
  const used = quota?.queries_today ?? 0;
  const pct = dailyLimit === -1 ? 100 : Math.min(100, Math.round((used / Math.max(1, dailyLimit)) * 100));

  const tier = quota?.membership_tier || user?.membership_tier || "free";
  const tierName = (quota?.membership_name || tier).toUpperCase();

  return (
    <header
      className="sticky top-0 z-20 h-[56px] flex items-center justify-between px-3 md:px-6 border-b border-[#141414] bg-black/85"
      data-testid="topbar"
    >
      <div className="flex items-center gap-2.5 min-w-0 pl-11 md:pl-0">
        <div className="w-[3px] h-4 bg-[#9D4EDD] rounded-full" />
        <h2 className="font-heading text-sm md:text-[15px] font-semibold tracking-tight truncate">
          {title}
        </h2>
      </div>

      <div className="flex items-center gap-2">
        {/* Quota */}
        <div
          className="hidden sm:flex items-center gap-2 border border-[#1a1a1a] bg-[#080808] px-2.5 py-1.5 rounded-lg"
          title="Günlük sorgu kotanız"
          data-testid="topbar-quota"
        >
          <Activity size={11} className="text-[#9D4EDD]" />
          <span className="font-mono text-[11px] text-[#EDEDED] whitespace-nowrap">
            {used}/{dailyLimit === -1 ? "∞" : dailyLimit}
          </span>
          <div className="w-12 h-[3px] bg-[#141414] overflow-hidden rounded-full">
            <div
              className="h-full bg-[#9D4EDD] transition-[width] duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Records badge */}
        <div
          className="hidden md:flex items-center gap-1.5 border border-[#1a1a1a] bg-[#080808] px-2.5 py-1.5 rounded-lg"
          title="Sistemdeki toplam kayıt"
          data-testid="topbar-records"
        >
          <Wallet size={11} className="text-[#00FF66]" />
          <span className="font-mono text-[11px] text-[#EDEDED]">8M+</span>
        </div>

        {/* Tier badge */}
        <div
          className={`flex items-center gap-1 border px-2.5 py-1.5 rounded-lg ${
            tier === "gold"
              ? "border-[#EAB308]/60 text-[#EAB308] bg-[#EAB308]/5"
              : tier === "silver"
              ? "border-[#A1A1AA]/40 text-[#EDEDED] bg-white/5"
              : tier === "bronze"
              ? "border-[#9D4EDD]/60 text-[#9D4EDD] bg-[#9D4EDD]/5"
              : "border-[#1a1a1a] text-[#A1A1AA] bg-[#080808]"
          }`}
          data-testid="topbar-tier"
        >
          <Crown size={10} />
          <span className="font-mono text-[10px] tracking-widest">{tierName}</span>
        </div>

        {/* User */}
        <div
          className="hidden sm:flex items-center gap-2 border border-[#1a1a1a] bg-[#080808] pl-1.5 pr-2.5 py-1 rounded-lg"
          data-testid="topbar-user"
        >
          <div className="w-6 h-6 bg-[#9D4EDD]/15 border border-[#9D4EDD]/40 text-[#9D4EDD] flex items-center justify-center font-heading font-bold text-[10px] rounded-md">
            {user?.username?.[0]?.toUpperCase() || "U"}
          </div>
          <span className="text-xs text-[#EDEDED] max-w-[110px] truncate">
            {user?.username}
          </span>
        </div>
      </div>
    </header>
  );
}
